import React, { useRef } from "react";
import { Grid, Typography, Button, Card, CardActionArea, CardContent, makeStyles, Divider, IconButton } from "@material-ui/core";
import MicIcon from "@material-ui/icons/Mic";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(4),
  },
  voiceCard: {
    border: "2px solid transparent",
    transition: "0.2s",
    display: "flex",
    alignItems: "center",
  },
  selectedCard: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  checkIcon: {
    marginLeft: "auto",
    marginRight: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  uploadBox: {
    border: `2px dashed ${theme.palette.secondary.main}`,
    borderRadius: 8,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: theme.palette.background.default,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    }
  },
}));

const stockVoices = [
  // Originales / Neutras
  { id: "alloy", name: "Locutor Energético (Neutro)", desc: "Masculino, dinámico y versátil.", sample: "/assets/voices/sample_alloy.mp3" },
  { id: "nova", name: "Narradora Calmada (Neutro)", desc: "Femenino, suave y profesional.", sample: "/assets/voices/sample_nova.mp3" },
  
  // México
  { id: "es-MX-1", name: "Voz Comercial (México)", desc: "Femenino, brillante, acento mexicano.", sample: "/assets/voices/sample_mx_f.mp3" },
  { id: "es-MX-2", name: "Locutor de Radio (México)", desc: "Masculino, grave, acento mexicano.", sample: "/assets/voices/sample_mx_m.mp3" },
  
  // Colombia
  { id: "es-CO-1", name: "Podcast (Colombia)", desc: "Femenino, cálida, acento colombiano.", sample: "/assets/voices/sample_co_f.mp3" },
  { id: "es-CO-2", name: "Voz Corporativa (Colombia)", desc: "Masculino, formal, acento colombiano.", sample: "/assets/voices/sample_co_m.mp3" },
  
  // Argentina
  { id: "es-AR-1", name: "Voz Expresiva (Argentina)", desc: "Femenino, natural, acento argentino.", sample: "/assets/voices/sample_ar_f.mp3" },
  { id: "es-AR-2", name: "Narrador Urbano (Argentina)", desc: "Masculino, cálido, acento argentino.", sample: "/assets/voices/sample_ar_m.mp3" },
  
  // Chile
  { id: "es-CL-1", name: "Narradora Fresca (Chile)", desc: "Femenino, juvenil, acento chileno.", sample: "/assets/voices/sample_cl_f.mp3" },
  { id: "es-CL-2", name: "Presentador (Chile)", desc: "Masculino, dinámico, acento chileno.", sample: "/assets/voices/sample_cl_m.mp3" },
  
  // Perú
  { id: "es-PE-1", name: "Voz Amable (Perú)", desc: "Femenino, cercana, acento peruano.", sample: "/assets/voices/sample_pe_f.mp3" },
  { id: "es-PE-2", name: "Locutor Confiable (Perú)", desc: "Masculino, seguro, acento peruano.", sample: "/assets/voices/sample_pe_m.mp3" },
  
  // Ecuador
  { id: "es-EC-1", name: "Voz Clara (Ecuador)", desc: "Femenino, educativa, acento ecuatoriano.", sample: "/assets/voices/sample_ec_f.mp3" },
  { id: "es-EC-2", name: "Narrador Institucional (Ecuador)", desc: "Masculino, profesional, acento ecuatoriano.", sample: "/assets/voices/sample_ec_m.mp3" },
  
  // República Dominicana
  { id: "es-DO-1", name: "Locutora Alegre (R. Dominicana)", desc: "Femenino, carismática, acento caribeño.", sample: "/assets/voices/sample_do_f.mp3" },
  { id: "es-DO-2", name: "Locutor Entusiasta (R. Dominicana)", desc: "Masculino, alegre, acento caribeño.", sample: "/assets/voices/sample_do_m.mp3" },
  
  // España
  { id: "es-ES-1", name: "Documental (España)", desc: "Femenino, elegante, acento castellano.", sample: "/assets/voices/sample_es_f.mp3" },
  { id: "es-ES-2", name: "Actor de Doblaje (España)", desc: "Masculino, profundo, acento castellano.", sample: "/assets/voices/sample_es_m.mp3" },
];

const StepVoice = ({ formData, updateFormData }) => {
  const classes = useStyles();
  const voiceInputRef = useRef(null);

  const handleVoiceFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      updateFormData("voiceSampleFile", file);
      updateFormData("voiceActor", null); // Override stock
    }
  };

  const playSample = (e, url, name) => {
    e.stopPropagation();
    // Simulate playing a local file
    const audio = new Audio(url);
    audio.volume = 0.5;
    // Falback to speech synthesis just in case the file doesn't exist during dev
    audio.play().catch(() => {
        if ('speechSynthesis' in window) {
          window.speechSynthesis.cancel();
          const msg = new SpeechSynthesisUtterance(`Hola, soy la voz de ${name}. Así sonaré en tu anuncio.`);
          window.speechSynthesis.speak(msg);
        }
    });
    toast.success(`Reproduciendo muestra: ${name}`);
  };

  return (
    <div>
      <div className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Configuración de la Voz
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          Selecciona una voz profesional de nuestro catálogo o clona tu propia voz para mayor personalización.
        </Typography>
        
        {/* Clonar Voz (Upload) */}
        <div 
          className={classes.uploadBox} 
          onClick={() => voiceInputRef.current && voiceInputRef.current.click()}
          style={{ marginBottom: 12 }}
        >
          <input 
            type="file" 
            accept="audio/mp3, audio/wav, audio/ogg" 
            hidden 
            ref={voiceInputRef} 
            onChange={handleVoiceFile} 
          />
          <MicIcon color="secondary" style={{ fontSize: 40 }} />
          <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
            Clonar Mi Voz
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Sube un archivo de audio (MP3/WAV) de 15 a 30 segundos donde hables claro y sin ruido de fondo.
          </Typography>
          {formData.voiceSampleFile && (
            <Typography variant="body1" style={{ color: "green", marginTop: 8, fontWeight: "bold" }}>
              ✓ Archivo cargado: {formData.voiceSampleFile.name}
            </Typography>
          )}
        </div>
        
        <div style={{ backgroundColor: "#f5f5f5", padding: "12px 16px", borderRadius: 8, marginBottom: 24 }}>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", textAlign: "center" }}>
            💡 <strong>Tip para mejor calidad:</strong> Graba un audio leyendo el siguiente texto de forma natural:<br/>
            <em style={{ color: "#333" }}>"Hola, estoy grabando esta muestra de voz para crear mi avatar digital. Me apasiona innovar, comunicar mis ideas con claridad y conectar con mi audiencia de forma auténtica. Me emociona ver cómo esta herramienta me ayudará a potenciar mis mensajes."</em>
          </Typography>
        </div>

        <Divider style={{ margin: "24px 0" }} />

        {!formData.voiceSampleFile && (
          <>
            <Typography variant="subtitle2" style={{ marginBottom: 16 }}>
              Voces Predefinidas (Stock):
            </Typography>
            <Grid container spacing={2}>
              {stockVoices.map(voice => {
                const isSelected = formData.voiceActor === voice.id && !formData.voiceSampleFile;
                return (
                  <Grid item xs={12} sm={6} key={voice.id}>
                    <Card 
                      className={`${classes.voiceCard} ${isSelected ? classes.selectedCard : ""}`}
                      onClick={() => updateFormData("voiceActor", voice.id)}
                    >
                      <CardActionArea style={{ display: "flex", alignItems: "center" }}>
                        <IconButton color="primary" onClick={(e) => playSample(e, voice.sample, voice.name)}>
                          <PlayCircleOutlineIcon fontSize="large" />
                        </IconButton>
                        <CardContent style={{ flexGrow: 1, padding: "16px 8px" }}>
                          <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                            {voice.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {voice.desc}
                          </Typography>
                        </CardContent>
                        {isSelected && <CheckCircleIcon className={classes.checkIcon} />}
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </div>
    </div>
  );
};

export default StepVoice;
